import {
  forwardRef,
  type HTMLAttributes,
  type ThHTMLAttributes,
  type TdHTMLAttributes,
} from "react";
import clsx from "clsx";
import { type VariantProps, cva } from "class-variance-authority";
import { cn } from "~/lib/utils";

const tableVariants = cva("text-sm w-full", {
  variants: {
    variant: {
      default: "",
      hoverable: [
        /** Targets all the `tr`s */
        "hover:[&_tr]:bg-muted",
      ],
      clickable: [
        /** Targets the `tr` of the `tbody` */
        "hover:[&>tbody>tr]:bg-muted hover:[&>tbody>tr]:cursor-pointer active:[&>tbody>tr]:bg-muted/60",
      ],
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export type TableVariantProps = VariantProps<typeof tableVariants>;

type TableProps = HTMLAttributes<HTMLTableElement> & TableVariantProps;

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <table
        ref={ref}
        className={cn(tableVariants({ variant, className }))}
        {...props}
      />
    );
  }
);

Table.displayName = "Table";

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  return (
    <thead
      ref={ref}
      className={clsx(
        "[&_tr]:border-t-2 [&_tr]:border-border [&>tr]:first:border-t-0",
        className
      )}
      {...props}
    />
  );
});

TableHeader.displayName = "TableHeader";

const TableHead = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  return (
    <th
      ref={ref}
      className={clsx(
        "h-12 px-4 text-sm font-semibold truncate select-none has-[button[role='checkbox']]:w-16 bg-muted",
        className
      )}
      {...props}
    />
  );
});

TableHead.displayName = "TableHead";

const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>((props, ref) => {
  return <tbody ref={ref} {...props} />;
});

TableBody.displayName = "TableBody";

const TableRow = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => {
  return (
    <tr
      ref={ref}
      className={clsx("border-t-border border-t-2 w-full bg-card", className)}
      {...props}
    />
  );
});

TableRow.displayName = "TableRow";

const TableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  return <td ref={ref} className={clsx("py-2 px-4", className)} {...props} />;
});

TableCell.displayName = "TableCell";

export { Table, TableHeader, TableHead, TableBody, TableRow, TableCell };
